import {
  type WorkflowAction,
  type UserId,
  type WorkflowStageId,
  type WorkflowStage,
  type Workflow,
  type AccountData,
  isNotDefined,
  getWorkflowAccountTimeline,
  WORKFLOW_PROSPECT_STAGE_ID,
  isDefined,
  type WorkflowHistoryItem,
  isNotEmpty,
  type LatLng,
  isEmpty,
  isNotEqual,
} from '@terros/common'

export type TimelineItem =
  | {
      type: 'action'
      action: WorkflowAction
      stage: WorkflowStage
      userId: UserId
      timestamp: number
      stageId: WorkflowStageId
      latlng?: LatLng
    }
  | {
      type: 'stage'
      stage: WorkflowStage
      userId?: UserId
      timestamp?: number
      isBackfilled?: boolean
      editTimelineIndicies?: number[]
    }

type BuildAccountTimelineProps = {
  workflow: Workflow
  account: Pick<AccountData, 'workflowHistory' | 'importId'> | undefined
  cutoffDate?: number
}

export const buildAccountTimeline = ({ workflow, account, cutoffDate }: BuildAccountTimelineProps): TimelineItem[] => {
  if (isNotDefined(account)) return []
  const timeline = getWorkflowAccountTimeline(account)
  if (isEmpty(timeline)) return []

  const initStage = workflow.getStage(WORKFLOW_PROSPECT_STAGE_ID)
  const result: TimelineItem[] = []

  if (
    isDefined(initStage) &&
    isDefined(timeline[0]) &&
    timeline[0].stageId !== initStage.id &&
    isNotDefined(account.importId)
  ) {
    result.push({
      type: 'stage',
      stage: initStage,
      isBackfilled: true,
    })
  }

  const groupedTimeline = groupTimelineHistoryItems(timeline)

  for (const group of groupedTimeline) {
    const lastItem = group.at(-1)
    if (isNotDefined(lastItem)) continue
    const action = workflow.getAction(lastItem.actionId)
    const stage = workflow.getStage(lastItem.stageId)
    if (isDefined(action) && isDefined(stage)) {
      result.push({
        type: 'action',
        action,
        stage,
        userId: lastItem.userId,
        timestamp: lastItem.timestamp,
        stageId: lastItem.stageId,
        latlng: lastItem.latlng,
      })
    }

    for (const [index, item] of group.entries()) {
      const stage = workflow.getStage(item.stageId)
      if (isNotDefined(stage)) continue

      if (stage.isPipelineStage && !stage.isInitial) {
        const prevPipelineStages = workflow.pipelineStages.filter((s) => !s.isInitial && s.order < stage.order)
        for (const prevPipelineStage of prevPipelineStages) {
          if (result.some((i) => i.type === 'stage' && i.stage.id === prevPipelineStage.id)) continue
          result.push({
            type: 'stage',
            stage: prevPipelineStage,
            isBackfilled: true,
          })
        }
      }

      const isLastItem = index === group.length - 1

      result.push({
        type: 'stage',
        stage,
        userId: isLastItem ? item.userId : undefined,
        timestamp: isLastItem ? item.timestamp : undefined,
        editTimelineIndicies: isLastItem ? group.map((i) => i.originalTimelineIndex) : undefined,
      })
    }
  }

  if (isDefined(cutoffDate)) {
    const cutoffIndex = result.findIndex((item) => isDefined(item.timestamp) && item.timestamp > cutoffDate)
    if (cutoffIndex !== -1) {
      const newResult = result.slice(cutoffIndex)
      const previousStage = result[cutoffIndex - 1]?.stage
      if (isDefined(previousStage)) {
        newResult.unshift({
          type: 'stage',
          stage: previousStage,
        })
      }
      return newResult
    }
  }

  return result
}

type GroupedHistoryItem = WorkflowHistoryItem & {
  originalTimelineIndex: number
}

export const groupTimelineHistoryItems = (timeline: WorkflowHistoryItem[]): GroupedHistoryItem[][] => {
  const groupedTimeline: GroupedHistoryItem[][] = [[]]
  for (const [index, item] of timeline.entries()) {
    const prevItem = groupedTimeline.at(-1)?.at(-1)
    if (shouldGroup(prevItem, item)) {
      groupedTimeline.at(-1)?.push({ ...item, originalTimelineIndex: index })
    } else {
      groupedTimeline.push([{ ...item, originalTimelineIndex: index }])
    }
    if (isDefined(item.actionId)) {
      groupedTimeline.push([])
    }
  }
  return groupedTimeline.filter(isNotEmpty)
}

const shouldGroup = (prevItem: WorkflowHistoryItem | undefined, item: WorkflowHistoryItem): boolean => {
  if (isDefined(prevItem)) {
    if (item.timestamp - prevItem.timestamp !== 1) return false
    if (item.userId !== prevItem.userId) return false
    if (isNotEqual(item.latlng, prevItem.latlng)) return false
    if (prevItem.stageId === WORKFLOW_PROSPECT_STAGE_ID) return false
  }
  return true
}
